import { useState } from "react";
import { AlertTriangle, Check, Database, Loader2, RotateCcw, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import { settingsApi, type DevCleanupOptions, type DevCleanupResult } from "@/lib/settings.api";
import { SettingsKpi } from "../components/SettingsKpi";
import { CLEANUP_CONFIRMATION, CLEANUP_OPTIONS, cleanupResultLabel } from "./maintenance.constants";
import { getApiErrorMessage } from "../settingsPage.helpers";
import { MaintenanceModePanel } from "./MaintenanceModePanel";
import { AttributionReassignmentPanel } from "./AttributionReassignmentPanel";

export function MaintenanceTab() {
  const { user } = useAuth();
  const [options, setOptions] = useState<DevCleanupOptions>({});
  const [confirmation, setConfirmation] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DevCleanupResult | null>(null);

  if (!user || user.role !== "super_admin") {
    return (
      <div className="border border-warning-border bg-warning-bg px-4 py-3 text-sm text-warning-text">
        Ces outils sont réservés au super administrateur.
      </div>
    );
  }

  const selectedCount = CLEANUP_OPTIONS.filter((option) => options[option.key]).length;
  const canRun = selectedCount > 0 && confirmation === CLEANUP_CONFIRMATION && !running;

  function toggleOption(key: keyof DevCleanupOptions) {
    setOptions((current) => ({ ...current, [key]: !current[key] }));
    setResult(null);
  }

  function selectAll() {
    setOptions(Object.fromEntries(CLEANUP_OPTIONS.map((option) => [option.key, true])));
    setResult(null);
  }

  function clearSelection() {
    setOptions({});
    setResult(null);
  }

  async function handleCleanup() {
    setRunning(true);
    setResult(null);
    try {
      const response = await settingsApi.devCleanup({ options, confirmation });
      setResult(response.data);
      setConfirmation("");
      toast.success("Données de test nettoyées.");
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Nettoyage impossible."));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-3">
        <SettingsKpi
          icon={Database}
          label="Sections sélectionnées"
          value={String(selectedCount)}
          tone={selectedCount > 0 ? "warning" : "neutral"}
        />
        <SettingsKpi icon={ShieldCheck} label="Accès requis" value="Super admin" />
        <SettingsKpi icon={Trash2} label="Mode" value="Irréversible" tone="danger" />
      </div>

      <MaintenanceModePanel />

      <AttributionReassignmentPanel />

      <section className="border border-danger-border bg-[repeating-linear-gradient(135deg,var(--danger-bg)_0,var(--danger-bg)_10px,var(--card)_10px,var(--card)_24px)]">
        <div className="grid gap-3 border-b border-danger-border px-4 py-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
          <div>
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-danger-text" />
              <h3 className="text-sm font-semibold text-danger-text">
                Nettoyage des données de développement
              </h3>
            </div>
            <p className="mt-1 max-w-3xl text-xs leading-5 text-danger-text">
              Utilisez cette zone pour vider les données de test créées pendant le développement.
              Les utilisateurs, rôles, paramètres, modules et catalogue de commissions sont
              conservés.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={selectAll}>
              Tout cocher
            </Button>
            <Button size="sm" variant="secondary" onClick={clearSelection}>
              Réinitialiser
            </Button>
          </div>
        </div>

        <div className="grid gap-3 p-3 md:grid-cols-2 xl:grid-cols-3">
          {CLEANUP_OPTIONS.map((option) => {
            const active = Boolean(options[option.key]);
            const toneClass =
              option.tone === "danger"
                ? "border-danger-border"
                : option.tone === "warning"
                  ? "border-warning-border"
                  : "border-border";

            return (
              <button
                key={option.key}
                type="button"
                onClick={() => toggleOption(option.key)}
                className={`min-h-full border bg-card p-3 text-left transition-colors ${
                  active ? toneClass : "border-border opacity-75 hover:opacity-100"
                }`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border ${
                      active
                        ? "border-brand-gold-dark bg-brand-gold-dark text-white"
                        : "border-border bg-surface-muted text-transparent"
                    }`}
                  >
                    <Check size={13} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">
                      {option.title}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                      {option.description}
                    </span>
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="border-t border-danger-border bg-card/80 px-4 py-4">
          <label className="mb-1.5 block text-[11.5px] font-medium text-body">
            Confirmation obligatoire
          </label>
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
            <Input
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              placeholder={`Tapez ${CLEANUP_CONFIRMATION}`}
              className="bg-card"
            />
            <Button variant="destructive" onClick={handleCleanup} disabled={!canRun}>
              {running ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              Nettoyer les données
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            En production, le serveur refuse cette action sauf activation explicite par variable
            d'environnement.
          </p>
        </div>
      </section>

      {result && <CleanupResultPanel result={result} />}
    </div>
  );
}

function CleanupResultPanel({ result }: { result: DevCleanupResult }) {
  const rows = [
    ...Object.entries(result.deleted).map(([key, count]) => ({
      label: cleanupResultLabel(key),
      count,
      type: "Supprimé",
    })),
    ...Object.entries(result.updated).map(([key, count]) => ({
      label: cleanupResultLabel(key),
      count,
      type: "Détaché",
    })),
    ...Object.entries(result.reset).map(([key, count]) => ({
      label: cleanupResultLabel(key),
      count,
      type: "Réinitialisé",
    })),
  ];

  return (
    <section className="border border-success-border bg-success-bg/40">
      <div className="flex items-center gap-2 border-b border-success-border px-4 py-3">
        <RotateCcw size={15} className="text-success-text" />
        <h3 className="text-sm font-semibold text-success-text">Résumé du dernier nettoyage</h3>
      </div>
      <div className="grid gap-2 p-3 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((row) => (
          <div key={`${row.type}-${row.label}`} className="border border-border bg-card px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{row.label}</p>
                <p className="text-[11px] text-muted-foreground">{row.type}</p>
              </div>
              <p className="text-lg font-semibold tabular-nums text-foreground">{row.count}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
